import { useCallback, useRef, useState } from "react";

interface UseCommentToggleFallbackOptions {
  onCommentToggle?: () => void;
}

export const useCommentToggleFallback = ({
  onCommentToggle,
}: UseCommentToggleFallbackOptions) => {
  const [isComposerOpen, setIsComposerOpen] = useState(false);
  const commentSectionRef = useRef<HTMLDivElement | null>(null);

  const scrollCommentSectionIntoView = useCallback(() => {
    const commentSection = commentSectionRef.current;
    if (!commentSection) return;

    const rect = commentSection.getBoundingClientRect();
    const viewportHeight =
      window.innerHeight || document.documentElement.clientHeight;
    const isFullyVisible = rect.top >= 0 && rect.bottom <= viewportHeight;

    if (!isFullyVisible) {
      commentSection.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, []);

  const handleCommentToggle = useCallback(() => {
    if (onCommentToggle) {
      onCommentToggle();
      return;
    }

    setIsComposerOpen((prev) => {
      const nextOpen = !prev;
      if (nextOpen) {
        scrollCommentSectionIntoView();
      }
      return nextOpen;
    });
  }, [onCommentToggle, scrollCommentSectionIntoView]);

  const closeComposer = useCallback(() => {
    setIsComposerOpen(false);
  }, []);

  return {
    isCommentActive: isComposerOpen,
    isComposerOpen,
    commentSectionRef,
    handleCommentToggle,
    closeComposer,
  };
};
